/**
 * The "Print / View" and "Share" pair that sits in every document's action bar.
 *
 * Every view page used to carry its own copy of the fetch-blob-and-open dance
 * plus its own busy flag. They are one component now, so the share behaviour
 * (and the awkward parts of it — see hooks/useSharePdf.js) is written once.
 *
 * Errors are handed back through `onError` rather than rendered here: each
 * page already has its own banner in its own place, and a sticky action bar
 * on a phone is the wrong spot for a sentence of explanation. The
 * "tap again" state needs no banner — the button relabels itself.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer, Share2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { Button } from './ui/Button.jsx';
import { useSharePdf } from '../hooks/useSharePdf.js';

/**
 * @param {object} props
 * @param {string} props.path - API path of the PDF endpoint
 * @param {object} [props.query] - query string params (the invoice's paper size, say)
 * @param {string} props.documentLabel - e.g. "Invoice VJ_26-27_0001"; the share sheet title
 * @param {string} props.fileBaseName - filename if the server sends none
 * @param {(message: string) => void} [props.onError]
 * @param {boolean} [props.compact] - icon-only, for a table row rather than an action bar
 *   (same convention as ThermalPrintButton, which sits beside it)
 */
export function PdfActions({ path, query, documentLabel, fileBaseName, onError, compact = false }) {
  // Same query key the rest of the app uses, so this is served from cache.
  // Only the shop name is wanted: a customer receiving a bill on WhatsApp
  // should see who it is from, and that name is configurable.
  const { data: shop } = useQuery({ queryKey: ['shop-settings'], queryFn: () => api.get('/shop-settings') });

  // Callers pass an object literal, which is a fresh reference every render —
  // compare by value so fetchPdf stays stable between renders.
  const queryKey = query ? JSON.stringify(query) : '';
  const stableQuery = useMemo(() => (queryKey ? JSON.parse(queryKey) : undefined), [queryKey]);

  const fetchPdf = useCallback(
    () => api.raw(path, stableQuery ? { query: stableQuery } : undefined),
    [path, stableQuery],
  );

  const { share, open, busy, awaitingTap, error } = useSharePdf(fetchPdf, {
    fallbackName: `${fileBaseName || 'document'}.pdf`,
    title: documentLabel,
    text: shop?.shop_name ? `${documentLabel} from ${shop.shop_name}` : documentLabel,
  });

  // Reported through the page's own banner. In an effect, not during render:
  // calling a parent's setState mid-render is a React error, not a style point.
  // The ref keeps a page that passes an inline arrow from being told the same
  // thing on every render.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const reported = useRef(null);
  useEffect(() => {
    if (error && error !== reported.current) {
      reported.current = error;
      onErrorRef.current?.(error);
    }
    if (!error) reported.current = null;
  }, [error]);

  if (compact) {
    return (
      <>
        <button
          type="button"
          onClick={open}
          disabled={busy}
          className="text-muted hover:text-primary disabled:opacity-50"
          aria-label="Print receipt"
        >
          <Printer size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={share}
          disabled={busy}
          className="text-muted hover:text-primary disabled:opacity-50"
          // There is no room for a label change here, so the state has to live
          // in the accessible name.
          aria-label={awaitingTap ? 'Receipt ready — tap again to share' : 'Share receipt'}
        >
          <Share2 size={16} aria-hidden="true" />
        </button>
      </>
    );
  }

  return (
    <>
      <Button variant="secondary" onClick={open} loading={busy} className="flex-1 sm:flex-none">
        <Printer size={17} aria-hidden="true" /> Print / View
      </Button>
      <Button variant="secondary" onClick={share} loading={busy} className="flex-1 sm:flex-none">
        <Share2 size={17} aria-hidden="true" /> {awaitingTap ? 'Tap to share' : 'Share'}
      </Button>
    </>
  );
}

export default PdfActions;
